/**
 * EditReimbursementModal — admin/manager/office edits a single reimbursement
 * inside a worker's timesheet (description, amount, category, date, status).
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, X } from "lucide-react";
import { Button, Modal, Input, Textarea, Select } from "../shared/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "paid", label: "Paid" },
];

const CATEGORIES = [
  { value: "fuel", label: "Fuel" },
  { value: "tolls", label: "Tolls" },
  { value: "parking", label: "Parking" },
  { value: "meals", label: "Meals" },
  { value: "tools", label: "Tools / Equipment" },
  { value: "ppe", label: "PPE" },
  { value: "consumables", label: "Consumables" },
  { value: "other", label: "Other" },
];

export const EditReimbursementModal = ({ timesheetId, reimbursement, onClose, onSaved }) => {
  const [form, setForm] = useState({
    description: "",
    category: "other",
    amount: "",
    date: "",
    status: "pending",
    no_receipt_reason: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!reimbursement) return;
    setForm({
      description: reimbursement.description || "",
      category: reimbursement.category || reimbursement.type || "other",
      amount: reimbursement.amount ?? "",
      date: reimbursement.date || "",
      status: reimbursement.status || "pending",
      no_receipt_reason: reimbursement.no_receipt_reason || "",
    });
  }, [reimbursement]);

  if (!reimbursement) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: form.amount === "" ? null : parseFloat(form.amount),
      };
      Object.keys(payload).forEach((k) => { if (payload[k] === undefined) delete payload[k]; });
      await axios.patch(
        `${API}/timesheets/${timesheetId}/reimbursement/${reimbursement.id}`,
        payload
      );
      toast.success("Reimbursement updated");
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Edit Reimbursement" size="md">
      <div className="space-y-3" data-testid="edit-reimbursement-modal">
        <Textarea
          label="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={2}
          testId="edit-reimb-description"
        />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} options={CATEGORIES} />
          <Input type="number" step="0.01" label="Amount ($)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} testId="edit-reimb-amount" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input type="date" label="Date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} options={STATUS} />
        </div>
        <Textarea label="No-receipt reason (if missing)" value={form.no_receipt_reason} onChange={(e) => setForm({ ...form, no_receipt_reason: e.target.value })} rows={2} />
        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="edit-reimb-save">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditReimbursementModal;
